import { produce } from "immer";
import React, { useMemo, useState } from "react";

import { ChartData, Cartesian } from "@examspark/types";

import BasicChart from "../../chart/BasicChart";
import CartesianShape from "../cartesian/CartesianShape";
import { Line } from "../cartesian/Line";
import { getImageCoordsFromCartesianCoords } from "../cartesian/utils";

import LineChartPoint from "./LineChartPoint";

interface Props {
  graph: Cartesian.Graph;
  data: ChartData;
  onChange?: (data: ChartData) => void;
}

const LineChart: React.FC<React.PropsWithChildren<Props>> = ({
  onChange,
  ...props
}) => {
  const [draggingIndex, setDraggingIndex] = useState<number>();
  const [draggingValue, setDraggingValue] = useState<number>();

  const graph = useMemo(() => {
    return produce(props.graph, (draft) => {
      draft.x.max = props.data.length - 1;
      draft.height = 400;
      draft.width = 500;
    });
  }, [props.data]);

  const lines: React.ReactNode[] = useMemo(() => {
    const _lines: React.ReactNode[] = [];

    for (const key in props.data) {
      if (!Object.prototype.hasOwnProperty.call(props.data, key)) continue;
      if (!props.data[Number(key) + 1]) continue;
      const idx = Number(key);

      /**
       * In case where a data point is being dragged, calculate line position
       * based on the current coordinate of the point being dragged. Need custom
       * calculations for the front and back side of the point.
       */
      if (draggingIndex !== undefined) {
        if (idx === draggingIndex) {
          _lines.push(
            <Line
              key={`${key}-a`}
              start={getImageCoordsFromCartesianCoords(graph, {
                x: idx,
                y: draggingValue || 0,
              })}
              end={getImageCoordsFromCartesianCoords(graph, {
                x: idx + 1,
                y: props.data[idx + 1].value,
              })}
            />,
          );

          continue;
        }

        if (idx === draggingIndex - 1) {
          _lines.push(
            <Line
              key={`${key}-b`}
              start={getImageCoordsFromCartesianCoords(graph, {
                x: idx,
                y: props.data[idx].value,
              })}
              end={getImageCoordsFromCartesianCoords(graph, {
                x: idx + 1,
                y: draggingValue || 0,
              })}
            />,
          );

          continue;
        }
      }

      _lines.push(
        <Line
          key={`${key}-c`}
          start={getImageCoordsFromCartesianCoords(graph, {
            x: idx,
            y: props.data[idx].value,
          })}
          end={getImageCoordsFromCartesianCoords(graph, {
            x: idx + 1,
            y: props.data[idx + 1].value,
          })}
        />,
      );
    }

    return _lines;
  }, [props.data, draggingIndex, draggingValue]);

  return (
    <BasicChart graph={graph} data={props.data}>
      <CartesianShape>{lines}</CartesianShape>
      {props.data.map((d, idx) => {
        return (
          <LineChartPoint
            key={idx}
            position={idx}
            label={d.label}
            value={d.value}
            onChange={(c) => {
              setDraggingIndex(undefined);
              onChange?.(
                produce(props.data, (draft) => {
                  draft[idx].value = c.y;
                }),
              );
            }}
            onDrag={(c) => {
              setDraggingIndex(idx);
              setDraggingValue(c.y);
            }}
          />
        );
      })}
    </BasicChart>
  );
};

export default LineChart;
